// HFP v3 Clinical drill-down — View B (build instruction section 5.3 with the
// chip grammar of 5.2; ticket TD-209).
//
// One page per development area, switched from the left-hand menu, clinical
// names throughout, layout [ label | Home | School ]. Each page carries only
// its own key as quiet swatches. Statement rows: collapsed = the verbatim
// clinical interpretation + score; expanded = the raw answer option per
// respondent. Means render in THIS view only, to 1 dp, each carrying its
// denominator.
//
// The sensory page is the load-bearing part: sense pills join the clinical
// label into the band word, carry provenance footers (RAISED BY PROFILE /
// PAIN SAFETY OVERRIDE) and the consider-masking marker; expanding a sense
// shows the two-column "how this rating was reached" narrative (which names
// the stage-1 band even when the label outranks it — the pill never does) and
// the boxed unscored threshold-and-response block; the profile items render
// after the scored statements as visibly unscored rows with honest empty
// states. Clinical label names only — the service-user persona names never
// render on this surface.
import React, { useMemo, useState } from 'react';
import {
  createHfpEngine,
  hfpChip,
  formatMean,
  RATING,
} from '../../utils/hfpV3Engine';
import {
  AREA_WORDS,
  RATING_TOKENS,
  STATEMENT_CHIP_TOKENS,
  UNSCORED_ROW_TOKENS,
  MASKING_MARKER,
  PROVENANCE_FOOTER_STYLE,
  DIFFERS_TOOLTIP,
  DIFFERS_BADGE_TOKENS,
} from '../../constants/hfpV3Tokens';
import crosswalk from '../../schemas/hfpV3/crosswalk.json';

const engine = createHfpEngine();
const CONTEXTS = ['home', 'school'];
const CONTEXT_LABEL = { home: 'Home', school: 'School' };
const RESPONDENT_LABEL = { home: 'parent / carer', school: 'teacher' };
const GRID = { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 172px 172px', alignItems: 'center' };

const DOMAIN_ROWS = crosswalk.filter((r) => r.level === 'domain');
const clinicalSubName = (domain, subdomain) => {
  const row = crosswalk.find(
    (r) => r.level === 'subdomain' && r.domain === domain && r.subdomain === subdomain
  );
  return (row && row.clinicalName) || subdomain;
};

const ROLE_PHRASE = {
  detect: 'detecting it',
  discriminate: 'telling it apart',
  use: 'using it day to day',
};

// Statement scores render as stored (−2, +0.5); only MEANS force one decimal.
const formatScore = (v) => `${v > 0 ? '+' : ''}${v}`;

// ---- statement-level answer chips (section 5.2) -----------------------------
function statementToken(option, isEnv) {
  if (isEnv && option.difficultyScore === 0) return STATEMENT_CHIP_TOKENS.envZeroOutline;
  if (!isEnv && option.difficultyScore !== null && option.difficultyScore > 0) {
    return STATEMENT_CHIP_TOKENS.strengthAnswer;
  }
  return STATEMENT_CHIP_TOKENS[option.tagColour] || STATEMENT_CHIP_TOKENS.grey;
}

function Chip({ tokens, children, title, className = '' }) {
  return (
    <span
      title={title}
      className={`inline-block rounded-lg px-2.5 py-1 text-xs font-semibold leading-snug ${className}`}
      style={{ background: tokens.bg, color: tokens.fg, border: `1px solid ${tokens.border}` }}
    >
      {children}
    </span>
  );
}

function AreaPill({ record, kind }) {
  const chip = hfpChip(record, { kind });
  return (
    <span className="inline-flex flex-col items-center gap-0.5 max-w-full">
      <Chip tokens={chip.tokens}>
        {chip.word}
        {chip.subText && (
          <span className="block text-[11px] font-medium opacity-80">{chip.subText}</span>
        )}
      </Chip>
      {chip.provenance && (
        <span style={PROVENANCE_FOOTER_STYLE}>{chip.provenance}</span>
      )}
      {chip.masking && (
        <button
          type="button"
          title={MASKING_MARKER.title}
          aria-label={`Consider masking: ${MASKING_MARKER.title}`}
          className="cursor-help"
          style={MASKING_MARKER.style}
        >
          ⚠ {MASKING_MARKER.text}
        </button>
      )}
    </span>
  );
}

function DiffersBadge({ direction }) {
  return (
    <span
      title={DIFFERS_TOOLTIP}
      className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={{
        background: DIFFERS_BADGE_TOKENS.bg,
        color: DIFFERS_BADGE_TOKENS.fg,
        border: `1px solid ${DIFFERS_BADGE_TOKENS.border}`,
      }}
    >
      ⚠ Differs{direction ? ` · worse at ${direction}` : ''}
    </span>
  );
}

// ---- the plain-language sensory narrative (section 5.3.2) --------------------
function senseNarrative(sense, senseItems, record, scored) {
  if (!record.valid && !record.label && !record.painOverride) {
    return "Too few of this sense's questions were answered to give a rating — not enough information.";
  }
  const parts = [];
  const answered = senseItems
    .filter((it) => ROLE_PHRASE[it.sensoryRole])
    .map((it) => ({ it, s: scored[it.itemId] }))
    .filter(({ s }) => s.score !== null);
  if (answered.length) {
    const bits = answered.map(({ it, s }) => `${ROLE_PHRASE[it.sensoryRole]} ${formatScore(s.score)}`);
    let scoredLine = `Scored answers — ${bits.join(', ')}`;
    if (sense === 'Body & Space') {
      scoredLine += ', plus the movement answers borrowed from Motor & Physical as one fourth input';
    }
    parts.push(`${scoredLine}.`);
    if (record.mean !== null) {
      parts.push(`Average ${formatMean(record.mean)} → ${AREA_WORDS[record.meanBand]}.`);
    }
  }
  if (record.painOverride) {
    parts.push(
      'Not noticing pain lifts this sense to Priority area whatever the average — a safety rule, because injuries and illness can go unnoticed.'
    );
    return parts.join(' ');
  }
  if (!record.thresholdTag) {
    const thr = senseItems.find((it) => it.sensoryRole === 'threshold');
    if (thr) {
      parts.push(
        record.thresholdAnswer
          ? 'The threshold answer is not an extreme, so no profile label applies and nothing further was asked. The rating is the scored average.'
          : 'The threshold question was not answered, so no profile label applies. The rating is the scored average.'
      );
    }
  } else if (record.label) {
    const rag = AREA_WORDS[engine.config.sensory_profile.label_rag[record.label]];
    if (record.raisedByProfile) {
      parts.push(
        `The threshold and follow-up answers give the profile ${record.label}, which carries ${rag} and supersedes the scored average — the rating reads ${AREA_WORDS[record.band]}, raised by profile.`
      );
    } else {
      parts.push(
        `The threshold and follow-up answers give the profile ${record.label} (${rag}); the scored average was already at least as severe, so the rating stays ${AREA_WORDS[record.band]}.`
      );
    }
  } else {
    parts.push(
      `The threshold answer sits at the ${record.thresholdTag === 'low' ? 'low' : 'high'}-threshold pole; the follow-up was not answered, so no profile label applies yet.`
    );
  }
  if (record.masking) {
    parts.push('Reacts strongly but puts up with it — flagged for the clinical team to consider masking.');
  }
  return parts.join(' ');
}

// ---- the boxed unscored threshold-and-response block --------------------------
function ProfileBlock({ senseItems, record, scored }) {
  const thr = senseItems.find((it) => it.sensoryRole === 'threshold');
  if (!thr) return null;
  const strat = senseItems.filter(
    (it) =>
      ['strategy_seek', 'strategy_reaction'].includes(it.sensoryRole) &&
      scored[it.itemId].rawAnswer &&
      it.conditionalOn &&
      scored[it.conditionalOn.itemId] &&
      scored[it.conditionalOn.itemId].rawAnswer === it.conditionalOn.optionId
  );
  const answerLabel = (it) => {
    const raw = scored[it.itemId].rawAnswer;
    if (!raw) return null;
    const opt = engine.instrument.options[it.itemId].find((o) => o.optionId === raw);
    return opt ? opt.label : raw;
  };
  const carries = record.label
    ? AREA_WORDS[engine.config.sensory_profile.label_rag[record.label]]
    : null;
  return (
    <div className="mt-2 rounded-lg border p-2.5 text-xs" style={{ borderColor: UNSCORED_ROW_TOKENS.separator, background: '#faf8fb' }}>
      <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
        Threshold and response · unscored — these produce the profile
      </p>
      <p className="font-semibold text-gray-700">{thr.statement}</p>
      <p className="text-gray-600 mb-1.5">
        Answered: <b>{answerLabel(thr) || '—'}</b>
        {record.thresholdTag
          ? ` — the ${record.thresholdTag}-threshold pole`
          : record.thresholdAnswer
            ? ' — no pole, nothing further asked'
            : ''}
      </p>
      {strat.map((it) => {
        const opt = engine.instrument.options[it.itemId].find(
          (o) => o.optionId === scored[it.itemId].rawAnswer
        );
        const axis = opt && (opt.responseAxis || (opt.profileTag === 'seek' ? 'active' : 'passive'));
        return (
          <div key={it.itemId} className="mb-1.5">
            <p className="font-semibold text-gray-700">{it.statement}</p>
            <p className="text-gray-600">
              Answered: <b>{answerLabel(it)}</b>
              {axis ? ` — ${axis} response` : ''}
            </p>
          </div>
        );
      })}
      {record.label && (
        <p className="border-t pt-1.5 text-gray-600" style={{ borderColor: UNSCORED_ROW_TOKENS.separator }}>
          Profile: <b>{record.label}</b> — carries {carries}
          {record.raisedByProfile
            ? ' and raised this sense’s band above its scored average.'
            : '; the scored average was already at least as severe.'}
        </p>
      )}
      {record.masking && (
        <p className="mt-1" style={{ color: MASKING_MARKER.style.color }}>
          ⚠ Consider masking — reacts strongly but puts up with it.
        </p>
      )}
    </div>
  );
}

// ---- statement rows ----------------------------------------------------------
function StatementRow({ item, scored, isEnv, defaultOpen = false, forceOpen = false }) {
  // Section 5.3: the default state opens the first area, first sub-domain,
  // FIRST STATEMENT — the first statement row of an open group starts expanded.
  const [openState, setOpen] = useState(defaultOpen);
  const open = openState || forceOpen;
  const isProfile = !!item.sensoryRole && !ROLE_PHRASE[item.sensoryRole];
  const bodyId = `stmt-${item.itemId.replace(/[^a-zA-Z0-9]/g, '-')}`;

  const cell = (context) => {
    const s = scored[context][item.itemId];
    if (s.excluded) {
      return <span className="text-xs italic text-gray-500">Not asked</span>;
    }
    if (isProfile) {
      if (!s.rawAnswer) {
        const fired =
          !item.conditionalOn ||
          scored[context][item.conditionalOn.itemId].rawAnswer === item.conditionalOn.optionId;
        return (
          <span className="text-xs italic text-gray-500">
            {item.sensoryRole === 'threshold'
              ? 'Not answered'
              : fired
                ? 'Not answered'
                : 'Not asked — the threshold answer did not fire this follow-up'}
          </span>
        );
      }
      const opt = engine.instrument.options[item.itemId].find((o) => o.optionId === s.rawAnswer);
      if (!opt) return <span className="text-xs italic text-gray-500">Not answered</span>;
      // Eve 2026-07-21: profile rows show the INTERPRETATION as the chip —
      // threshold pole (low/high) or response axis (active/passive), coloured
      // yellow when the answer tags; the raw answer lives in the title and the
      // expanded panel.
      if (item.sensoryRole === 'threshold') {
        return opt.profileTag ? (
          <Chip tokens={RATING_TOKENS.support} title={opt.label}>
            {opt.profileTag === 'low' ? 'Low threshold' : 'High threshold'}
          </Chip>
        ) : (
          <Chip tokens={UNSCORED_ROW_TOKENS.chip} title="Not an extreme — no tag, nothing further asked">
            {opt.label}
          </Chip>
        );
      }
      const axis =
        opt.responseAxis || (opt.profileTag === 'seek' ? 'active' : 'passive');
      return (
        <Chip tokens={RATING_TOKENS.support} title={opt.label}>
          {axis === 'active' ? 'Active response' : 'Passive response'}
          {opt.raisesMasking ? ' · masking' : ''}
        </Chip>
      );
    }
    if (s.rawAnswer === null || s.score === null) {
      return <span className="text-xs italic text-gray-500">{s.excluded ? 'Not asked' : 'Not answered'}</span>;
    }
    const opt = engine.instrument.options[item.itemId].find((o) => o.optionId === s.rawAnswer);
    if (!opt) return <span className="text-xs italic text-gray-500">Not answered</span>;
    return (
      <Chip tokens={statementToken(opt, isEnv)} title={opt.clinicalInterpretation}>
        {opt.uiTagText}
      </Chip>
    );
  };

  return (
    <div
      className="border-t"
      style={isProfile ? { borderTopStyle: 'dashed', borderColor: UNSCORED_ROW_TOKENS.separator, background: '#fcfbfd' } : { borderColor: '#f5eef6' }}
    >
      <div style={GRID}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 py-2 pl-7 pr-2 text-left min-w-0"
        >
          <span aria-hidden="true" className="text-gray-400 text-[10px]">{open ? '▼' : '▶'}</span>
          <span className="text-[13px] font-semibold text-gray-700 truncate">
            {!item.code.startsWith('TD-') && (
              <span className="font-mono font-normal text-gray-500 mr-1.5">{item.code}</span>
            )}
            {item.clinItemLabel}
          </span>
          {isProfile && (
            <span
              className="rounded px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide"
              style={{ background: UNSCORED_ROW_TOKENS.chip.bg, color: UNSCORED_ROW_TOKENS.chip.fg, border: `1px dashed ${UNSCORED_ROW_TOKENS.chip.border}` }}
            >
              {UNSCORED_ROW_TOKENS.badgeText}
            </span>
          )}
        </button>
        {CONTEXTS.map((c) => (
          <div key={c} className="px-2 py-1.5 text-center border-l" style={{ borderColor: '#f5eef6' }}>
            {cell(c)}
          </div>
        ))}
      </div>
      {open && (
        <div id={bodyId} className="mx-7 mb-3 rounded-lg border bg-white p-3 text-xs" style={{ borderColor: '#ece4ed' }}>
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1">Question asked</p>
          <p className="italic text-gray-500 mb-2">{item.questionStem}</p>
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1">
            {isProfile ? 'Statement' : 'Statement rated'}
          </p>
          <p className="text-sm text-gray-800">{item.statement}</p>
          {item.examples && <p className="mt-1 text-gray-500">{item.examples}</p>}
          <div className="grid grid-cols-2 gap-3 mt-3">
            {CONTEXTS.map((c) => {
              const s = scored[c][item.itemId];
              const opt = s.rawAnswer
                ? engine.instrument.options[item.itemId].find((o) => o.optionId === s.rawAnswer)
                : null;
              return (
                <div key={c} className="rounded-lg border p-2.5" style={{ borderColor: '#f1eaf2', background: '#f7f4fa' }}>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-1.5">
                    {CONTEXT_LABEL[c]} <span className="font-medium normal-case text-gray-500">· {RESPONDENT_LABEL[c]}</span>
                  </p>
                  {s.excluded ? (
                    <span className="italic text-gray-500">Not asked of this respondent</span>
                  ) : !opt ? (
                    <span className="italic text-gray-500">Not answered</span>
                  ) : isProfile ? (
                    <>
                      <Chip tokens={UNSCORED_ROW_TOKENS.chip}>{opt.label}</Chip>
                      <p className="mt-1.5 text-gray-500">{opt.clinicalInterpretation}</p>
                    </>
                  ) : (
                    <>
                      <Chip tokens={statementToken(opt, isEnv)}>{opt.uiTagText}</Chip>
                      <p className="mt-1.5 text-gray-500">{opt.clinicalInterpretation}</p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- sub-domain / sense groups ------------------------------------------------
function SubdomainGroup({ domain, subdomain, items, results, scored, senses, differs, defaultOpen, forceOpen = false }) {
  const [openState, setOpen] = useState(defaultOpen);
  const open = openState || forceOpen;
  const isSensory = domain === 'Sensory';
  const isEnv = domain === 'Environmental Factors';
  const groupId = `sub-${subdomain.replace(/[^a-zA-Z0-9]/g, '-')}`;
  const senseDiffers = isSensory ? differs.sensory[subdomain] : null;
  const functionalDiffers = differs.functional[`${domain}::${subdomain}`];
  const profileDisagreement =
    senseDiffers && (senseDiffers.via || []).includes('profile_outcome');

  const scoredItems = items.filter((it) => !it.sensoryRole || ROLE_PHRASE[it.sensoryRole]);
  // Eve 2026-07-21: a follow-up the threshold never fired (in either context)
  // does not render at all — a clinician only sees the branch that was asked.
  const profileItems = items.filter((it) => {
    if (!it.sensoryRole || ROLE_PHRASE[it.sensoryRole]) return false;
    if (it.sensoryRole === 'threshold' || !it.conditionalOn) return true;
    return CONTEXTS.some(
      (c) => scored[c][it.conditionalOn.itemId].rawAnswer === it.conditionalOn.optionId
    );
  });

  return (
    <div className="border-t" style={{ borderColor: '#efe6f0' }}>
      <div style={GRID}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={groupId}
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 py-2.5 pl-4 pr-2 text-left min-w-0"
        >
          <span aria-hidden="true" className="text-gray-400 text-xs">{open ? '▼' : '▶'}</span>
          <span className="text-sm font-semibold text-gray-800 truncate">
            {clinicalSubName(domain, subdomain)}
          </span>
          {(senseDiffers || functionalDiffers) && (
            <DiffersBadge direction={(senseDiffers || functionalDiffers).worseAt} />
          )}
        </button>
        {CONTEXTS.map((c) => (
          <div key={c} className="px-2 py-1.5 text-center border-l" style={{ borderColor: '#f5eef6' }}>
            {isSensory ? (
              <AreaPill record={senses[c][subdomain]} kind="sense" />
            ) : (
              <AreaPill record={results[c]} kind={isEnv ? 'env' : 'functional'} />
            )}
          </div>
        ))}
      </div>
      {open && (
        <div id={groupId}>
          {isSensory && profileDisagreement && (
            <div className="mx-4 mb-2 rounded-lg border px-3 py-2 text-xs" style={{ background: '#fdf3f3', borderColor: '#f5dede', color: '#b3302c' }}>
              ⚠ Home and school disagree on the threshold answer — the two contexts derive
              different sensory profiles. Worth exploring at review; it never changes a rating.
            </div>
          )}
          {isSensory && (
            <div className="mx-4 mb-2 grid grid-cols-2 gap-3">
              {CONTEXTS.map((c) => (
                <div key={c} className="rounded-lg border p-2.5" style={{ borderColor: '#f1eaf2', background: '#faf8fb' }}>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1">
                    {CONTEXT_LABEL[c]} — how this rating was reached
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {senseNarrative(subdomain, items, senses[c][subdomain], scored[c])}
                  </p>
                  <ProfileBlock senseItems={items} record={senses[c][subdomain]} scored={scored[c]} />
                </div>
              ))}
            </div>
          )}
          {scoredItems.map((it, idx) => (
            <StatementRow
              key={it.itemId}
              item={it}
              scored={scored}
              isEnv={isEnv}
              defaultOpen={defaultOpen && idx === 0}
              forceOpen={forceOpen}
            />
          ))}
          {profileItems.length > 0 && (
            <div className="pl-7 py-1.5" style={{ background: '#fcfbfd' }}>
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                Threshold and response · unscored — these produce the profile, never the mean
              </span>
            </div>
          )}
          {profileItems.map((it) => (
            <StatementRow key={it.itemId} item={it} scored={scored} isEnv={false} forceOpen={forceOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- page key (quiet swatches, page-scoped) ------------------------------------
function PageKey({ isSensory, isEnv }) {
  const swatch = (word, tokens) => (
    <span key={word} className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
      <span className="w-3 h-3 rounded flex-none" style={{ background: tokens.bg, border: `1px solid ${tokens.border}` }} />
      {word}
    </span>
  );
  const areaWords = isEnv
    ? [['Facilitators in place', RATING_TOKENS.strength], ['No barriers', RATING_TOKENS.none], ['Barriers present', RATING_TOKENS.support], ['Severe barriers', RATING_TOKENS.priority], ['Insufficient data', RATING_TOKENS.insufficient]]
    : [['Strength', RATING_TOKENS.strength], ['No difficulty', RATING_TOKENS.none], ['Support need', RATING_TOKENS.support], ['Priority area', RATING_TOKENS.priority], ['Insufficient data', RATING_TOKENS.insufficient]];
  return (
    <div className="rounded-xl border px-4 py-2.5 mb-3" style={{ borderColor: '#efe6f0', background: '#fcfbfd' }}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 w-28 flex-none">
          {isSensory ? 'Sense ratings' : 'Area ratings'}
        </span>
        {areaWords.map(([w, t]) => swatch(w, t))}
      </div>
      {isSensory && (
        <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
          A sense is rated in two stages. The scored answers make an average → band. The unscored
          threshold question (only the two extreme answers do anything) plus the response follow-up
          derive a profile label on two axes — <b>Seeking</b> = active response to a high threshold ·{' '}
          <b>Avoiding</b> = active response to a low threshold · <b>Sensitivity</b> = passive response
          to a low threshold · <b>Low Registration</b> = passive response to a high threshold.
          Seeking and Avoiding carry Priority area; Sensitivity and Low Registration carry Support
          need. When a label is present it is shown as the tag and{' '}
          <b>supersedes the average when it is more severe</b> (never the other way) — the colour is
          the resolved band, marked <b>RAISED BY PROFILE</b> when the label lifted it. Pain has its
          own safety override.
        </p>
      )}
    </div>
  );
}

/**
 * @param {object} answersByContext  { home: {itemId: optionId}, school: {...} }
 * @param {string} initialDomain     internal domain key to open (optional)
 * @param {string} initialArea       internal sub-domain / sense to open (optional)
 */
export default function HfpV3ClinicalView({ answersByContext, initialDomain, initialArea }) {
  const domainKeys = DOMAIN_ROWS.map((d) => d.domain);
  const [current, setCurrent] = useState(
    domainKeys.includes(initialDomain) ? initialDomain : domainKeys[0]
  );
  // Print / EHCP export (instruction section 8 [build]): expand-all so the
  // printed page carries every statement, caveat and provenance footer; the
  // chips already read as words, so ratings survive without colour.
  const [expandAll, setExpandAll] = useState(false);
  const handlePrint = () => {
    setExpandAll(true);
    setTimeout(() => {
      if (typeof window !== 'undefined' && window.print) window.print();
    }, 80);
  };

  const model = useMemo(() => {
    const scored = {
      home: engine.scoreContext(answersByContext.home || {}, 'home'),
      school: engine.scoreContext(answersByContext.school || {}, 'school'),
    };
    const functional = {
      home: engine.classifyFunctional(scored.home),
      school: engine.classifyFunctional(scored.school),
    };
    const senses = {
      home: engine.sensoryResolve(scored.home),
      school: engine.sensoryResolve(scored.school),
    };
    return {
      scored,
      functional,
      senses,
      sensoryDomain: {
        home: engine.sensoryDomainSummary(senses.home),
        school: engine.sensoryDomainSummary(senses.school),
      },
      differs: engine.differs(scored.home, scored.school),
    };
  }, [answersByContext]);

  const isSensory = current === 'Sensory';
  const isEnv = current === 'Environmental Factors';
  const domainRow = DOMAIN_ROWS.find((d) => d.domain === current);

  const subdomains = useMemo(() => {
    const seen = [];
    engine.instrument.items.forEach((it) => {
      if (it.domain === current && !seen.some((s) => s.subdomain === it.subdomain)) {
        seen.push({ subdomain: it.subdomain, items: [] });
      }
    });
    seen.forEach((s) => {
      s.items = engine.instrument.items.filter(
        (it) => it.domain === current && it.subdomain === s.subdomain
      );
    });
    return seen;
  }, [current]);

  return (
    <div className="hfpv3-clinical grid gap-5 items-start" style={{ gridTemplateColumns: '220px minmax(0,1fr)' }}>
      <style>{`
        @media (max-width: 760px) {
          .hfpv3-clinical { grid-template-columns: 1fr !important; }
          .hfpv3-clinical nav { position: static !important; }
        }
        .hfpv3-scroll { overflow-x: auto; }
        .hfpv3-scroll > div { min-width: 560px; }
        @media print {
          .hfpv3-clinical { display: block !important; }
          .hfpv3-clinical nav, .hfpv3-controls { display: none !important; }
          .hfpv3-clinical main { border: none !important; }
          .hfpv3-clinical button { pointer-events: none; }
        }
      `}</style>
      <nav className="bg-white rounded-xl border p-3 sticky top-4" style={{ borderColor: '#e6e2ea' }} aria-label="Clinical view pages">
        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 px-2 mb-2">
          Clinical view — pages
        </p>
        {DOMAIN_ROWS.map((d) => (
          <button
            key={d.domain}
            type="button"
            aria-current={d.domain === current ? 'page' : undefined}
            onClick={() => setCurrent(d.domain)}
            className={`block w-full text-left rounded-lg px-3 py-2 text-[13px] mb-0.5 ${
              d.domain === current ? 'bg-primary/10 font-bold text-primary' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {d.clinicalName}
          </button>
        ))}
      </nav>

      <main className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#e6e2ea' }}>
        <div className="px-5 pt-4 pb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#9a7d2f' }}>
              Clinical HFP · by development area
            </p>
            <h2 className="text-xl font-bold" style={{ color: '#43213e' }}>{domainRow.clinicalName}</h2>
          </div>
          <div className="hfpv3-controls flex items-center gap-2 flex-none pt-1">
            <button
              type="button"
              onClick={() => setExpandAll((v) => !v)}
              className="rounded-lg border px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
              style={{ borderColor: '#e6e2ea' }}
            >
              {expandAll ? 'Collapse all' : 'Expand all'}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="rounded-lg border px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
              style={{ borderColor: '#e6e2ea' }}
            >
              Print / export
            </button>
          </div>
        </div>
        <p className="px-5 -mt-2 pb-2 text-xs text-gray-500">
          Ratings use thresholds that are under clinical calibration.
        </p>
        <div className="px-5 hfpv3-scroll">
          <div>
          <PageKey isSensory={isSensory} isEnv={isEnv} />
          <div style={GRID} className="border-b-2 pb-1" >
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 pl-4">
              Sub-domain · statement
            </span>
            {CONTEXTS.map((c) => (
              <span key={c} className="text-sm font-bold text-center" style={{ color: '#43213e' }}>
                {CONTEXT_LABEL[c]}
              </span>
            ))}
          </div>
          {/* domain header row */}
          <div style={GRID} className="border-b" >
            <span className="text-sm font-bold py-2.5 pl-4" style={{ color: '#43213e' }}>
              {domainRow.clinicalName}
            </span>
            {CONTEXTS.map((c) => (
              <div key={c} className="px-2 py-1.5 text-center border-l" style={{ borderColor: '#f5eef6' }}>
                {isSensory ? (
                  <AreaPill record={model.sensoryDomain[c]} kind="sensoryDomain" />
                ) : (
                  <AreaPill
                    record={model.functional[c][current] || { rating: RATING.NOT_APPLICABLE, mean: null }}
                    kind={isEnv ? 'env' : 'functional'}
                  />
                )}
              </div>
            ))}
          </div>
          {subdomains.map((s, i) => (
            <SubdomainGroup
              key={s.subdomain}
              domain={current}
              subdomain={s.subdomain}
              items={s.items}
              results={{
                home: (model.functional.home[current] || { subdomains: {} }).subdomains[s.subdomain],
                school: (model.functional.school[current] || { subdomains: {} }).subdomains[s.subdomain],
              }}
              scored={model.scored}
              senses={model.senses}
              differs={model.differs}
              defaultOpen={initialArea ? s.subdomain === initialArea : i === 0}
              forceOpen={expandAll}
            />
          ))}
          <div className="h-4" />
          </div>
        </div>
      </main>
    </div>
  );
}
